import { useState } from "react";
import { formatMoney } from "@/lib/orderUtils";

/**
 * Shared modifier picker modal.
 * Used by Counter, Kiosk and QR menu when a menu item has modifier_groups.
 *
 * Props:
 *   item: MenuItem with modifier_groups
 *   onCancel: () => void
 *   onConfirm: (chosenMods: Array<{group_name, option_name, price_delta}>, finalPrice: number) => void
 */
export const ModifierPicker = ({ item, onCancel, onConfirm }) => {
    const [selected, setSelected] = useState(() => {
        const init = {};
        for (const g of item.modifier_groups || []) {
            init[g.id] = g.type === "multi" ? [] : null;
        }
        return init;
    });
    // Sub-picker selections: { [optionId]: subOptionId | null }
    const [subs, setSubs] = useState({});

    const toggle = (group, optId) => {
        setSelected((s) => {
            if (group.type === "multi") {
                const cur = s[group.id] || [];
                const has = cur.includes(optId);
                if (has) {
                    // Deselecting → also clear the sub-choice for this option
                    setSubs((ss) => { const next = { ...ss }; delete next[optId]; return next; });
                    return { ...s, [group.id]: cur.filter((x) => x !== optId) };
                }
                if (group.max_select && cur.length >= group.max_select) return s;
                return { ...s, [group.id]: [...cur, optId] };
            }
            const wasSelected = s[group.id] === optId;
            // If we're switching away from a previously-selected option, drop its sub-choice.
            if (!wasSelected) {
                setSubs((ss) => {
                    const next = { ...ss };
                    for (const o of group.options || []) if (o.id !== optId) delete next[o.id];
                    return next;
                });
            } else {
                setSubs((ss) => { const next = { ...ss }; delete next[optId]; return next; });
            }
            return { ...s, [group.id]: wasSelected ? null : optId };
        });
    };

    const pickSub = (optId, subOptId) => {
        setSubs((s) => ({ ...s, [optId]: s[optId] === subOptId ? null : subOptId }));
    };

    // Options that are currently selected in any group — used to know
    // when a sub-picker should appear + when it needs a satisfying pick.
    const isOptSelected = (g, optId) =>
        g.type === "multi" ? (selected[g.id] || []).includes(optId) : selected[g.id] === optId;

    const groupOk = (g) => {
        // Base group must be satisfied first
        if (g.type === "single_required" && !selected[g.id]) return false;
        if (g.type === "multi") {
            const n = (selected[g.id] || []).length;
            const min = g.min_select || 0;
            const max = g.max_select ?? Infinity;
            if (n < min || n > max) return false;
        }
        // Every SELECTED option with a required sub_group must have its sub picked.
        for (const opt of g.options || []) {
            if (!opt.sub_group) continue;
            if (!isOptSelected(g, opt.id)) continue;
            if (opt.sub_group.type === "single_required" && !subs[opt.id]) return false;
        }
        return true;
    };
    const allRequiredFilled = (item.modifier_groups || []).every(groupOk);

    // Compute chosen modifiers + final price (top-level + any sub-picks).
    const chosenMods = [];
    let delta = 0;
    const push = (label, opt) => {
        chosenMods.push({ group_name: label, option_name: opt.name, price_delta: opt.price_delta || 0 });
        delta += opt.price_delta || 0;
    };
    for (const g of item.modifier_groups || []) {
        const pushOpt = (opt) => {
            push(g.name, opt);
            const subId = subs[opt.id];
            if (subId && opt.sub_group) {
                const subOpt = (opt.sub_group.options || []).find((s) => s.id === subId);
                if (subOpt) push(`${g.name} · ${opt.name}`, subOpt);
            }
        };
        const v = selected[g.id];
        if (Array.isArray(v)) v.forEach((oid) => { const o = g.options.find((x) => x.id === oid); if (o) pushOpt(o); });
        else if (v) { const o = g.options.find((x) => x.id === v); if (o) pushOpt(o); }
    }
    const finalPrice = Math.round((item.price + delta) * 100) / 100;

    return (
        <div
            className="fixed inset-0 bg-[#050505] z-50 overflow-y-auto"
            data-testid="modifier-picker"
        >
            <div className="min-h-screen flex flex-col">
                <div className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#050505]">
                    <button
                        onClick={onCancel}
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold"
                        data-testid="picker-cancel"
                    >
                        ← Cancel
                    </button>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Customise</span>
                </div>
                <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-8">
                    <p className="eyebrow">// {item.name}</p>
                    <h2 className="font-display text-[40px] leading-[0.95] mt-3 mb-8">
                        Build it your <span className="text-gold">way.</span>
                    </h2>

                    {(item.modifier_groups || []).map((g) => (
                        <div key={g.id} className="mb-6">
                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--border)]">
                                <span className="font-display text-[18px]">{g.name}</span>
                                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted" data-testid={`picker-group-hint-${g.id}`}>
                                    {g.type === "single_required" && "Pick one (required)"}
                                    {g.type === "single_optional" && "Pick one (optional)"}
                                    {g.type === "multi" && (() => {
                                        const min = g.min_select || 0;
                                        const max = g.max_select;
                                        const n = (selected[g.id] || []).length;
                                        if (min > 0 && max) return `Pick ${min === max ? min : `${min}–${max}`} · ${n}/${max}`;
                                        if (min > 0) return `Pick at least ${min} · ${n} chosen`;
                                        if (max) return `Pick up to ${max} · ${n}/${max}`;
                                        return `Pick any · ${n} chosen`;
                                    })()}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {(g.options || []).map((opt) => {
                                    const isOn =
                                        g.type === "multi"
                                            ? (selected[g.id] || []).includes(opt.id)
                                            : selected[g.id] === opt.id;
                                    const showSubs = isOn && opt.sub_group && (opt.sub_group.options || []).length > 0;
                                    return (
                                        <div key={opt.id} className={showSubs ? "col-span-2 sm:col-span-3" : ""}>
                                        <button
                                            onClick={() => toggle(g, opt.id)}
                                            className={`w-full p-4 border-2 text-left transition-colors ${
                                                isOn
                                                    ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]"
                                                    : "border-[var(--border-strong)] bg-[#0e0e0e] hover:border-[var(--gold)] text-[var(--text)]"
                                            }`}
                                            data-testid={`picker-opt-${opt.id}`}
                                        >
                                            <div className={`text-base font-display ${isOn ? "text-[var(--bg)]" : "text-[var(--text)]"}`}>
                                                {opt.name}
                                            </div>
                                            {opt.description && (
                                                <div className={`text-[12px] leading-snug mt-1 ${isOn ? "text-[var(--bg)]/75" : "text-[var(--text)]/65"}`}>
                                                    {opt.description}
                                                </div>
                                            )}
                                            {opt.price_delta !== 0 && (
                                                <div className={`font-mono text-[12px] mt-2 ${isOn ? "text-[var(--bg)] font-bold" : "text-gold"}`}>
                                                    {opt.price_delta > 0 ? "+" : ""}
                                                    {formatMoney(opt.price_delta)}
                                                </div>
                                            )}
                                            {isOn && (
                                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bg)] mt-2 font-bold">
                                                    ✓ Selected
                                                </div>
                                            )}
                                        </button>
                                        {showSubs && (
                                            <div
                                                className="mt-2 mb-2 p-3 border border-[var(--gold)]/40 bg-[var(--gold)]/5"
                                                data-testid={`picker-sub-${opt.id}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
                                                        {opt.sub_group.name || `${opt.name} — pick one`}
                                                    </span>
                                                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted">
                                                        {opt.sub_group.type === "single_required" ? "Required" : "Optional"}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                    {(opt.sub_group.options || []).map((so) => {
                                                        const onSub = subs[opt.id] === so.id;
                                                        return (
                                                            <button
                                                                key={so.id}
                                                                onClick={() => pickSub(opt.id, so.id)}
                                                                className={`p-2 border text-left transition-colors ${
                                                                    onSub
                                                                        ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]"
                                                                        : "border-[var(--border-strong)] hover:border-[var(--gold)]"
                                                                }`}
                                                                data-testid={`picker-sub-opt-${so.id}`}
                                                            >
                                                                <div className="text-sm font-display leading-tight">{so.name}</div>
                                                                {so.price_delta !== 0 && (
                                                                    <div className={`font-mono text-[10px] mt-0.5 ${onSub ? "text-[var(--bg)]" : "text-gold"}`}>
                                                                        {so.price_delta > 0 ? "+" : ""}{formatMoney(so.price_delta)}
                                                                    </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <div className="border-t border-[var(--border)] pt-4 flex items-center justify-between mb-4">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Total</span>
                        <span className="font-mono text-[24px] text-gold" data-testid="picker-total">
                            {formatMoney(finalPrice)}
                        </span>
                    </div>

                    <button
                        onClick={() => onConfirm(chosenMods, finalPrice)}
                        disabled={!allRequiredFilled}
                        className="btn-primary w-full justify-center text-base py-4 disabled:opacity-30"
                        data-testid="picker-confirm"
                    >
                        Add to basket →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModifierPicker;
